/**
 * « Activer ce poste de caisse » (design, section 4.1) : code de 8
 * caracteres en gros, nom du poste, un seul bouton principal.
 */
import { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ACTIVATION_CODE_PATTERN, NAME_MAX_LENGTH, cleanActivationCode, enrollTerminal, enrollmentErrorMessage } from './enrollment';
import { palette } from './theme';

const CODE_LENGTH = 8;

export default function EnrollScreen({ db, api, onEnrolled }) {
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const codeInput = useRef(null);

    const canSubmit = ACTIVATION_CODE_PATTERN.test(code) && name.trim().length > 0 && !submitting;

    async function handleSubmit(event) {
        event.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const terminal = await enrollTerminal({ db, api, code, name });
            onEnrolled(terminal);
        } catch (err) {
            setError(enrollmentErrorMessage(err));
            setSubmitting(false);
        }
    }

    return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Paper
                component="form"
                onSubmit={handleSubmit}
                noValidate
                variant="outlined"
                sx={{ width: '100%', maxWidth: 640, p: 5, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
                <Typography component="h1" sx={{ fontSize: 32, fontWeight: 800 }}>
                    Activer ce poste de caisse
                </Typography>

                <Box>
                    <Typography component="label" htmlFor="code-activation" sx={{ fontSize: 18, fontWeight: 600, display: 'block', mb: 1 }}>
                        Code d&apos;activation
                    </Typography>
                    <Box
                        onClick={() => codeInput.current && codeInput.current.focus()}
                        sx={{
                            position: 'relative', display: 'flex', gap: 1,
                            borderRadius: 1, '&:focus-within': { outline: `3px solid ${palette.primary}`, outlineOffset: 4 },
                        }}
                    >
                        {Array.from({ length: CODE_LENGTH }, (_, i) => (
                            <Box
                                key={i}
                                aria-hidden="true"
                                sx={{
                                    width: 56, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: `2px solid ${i === code.length ? palette.primary : palette.border}`, borderRadius: 1,
                                    bgcolor: palette.surface, fontSize: 40, fontWeight: 800, fontFamily: 'monospace',
                                }}
                            >
                                {code[i] || ''}
                            </Box>
                        ))}
                        <input
                            id="code-activation"
                            ref={codeInput}
                            value={code}
                            onChange={(e) => setCode(cleanActivationCode(e.target.value))}
                            autoComplete="off"
                            autoCapitalize="characters"
                            spellCheck={false}
                            inputMode="text"
                            maxLength={CODE_LENGTH}
                            autoFocus
                            style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'text' }}
                        />
                    </Box>
                </Box>

                <TextField
                    id="nom-poste"
                    label="Nom du poste"
                    placeholder="Caisse 1"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    InputProps={{ sx: { fontSize: 22 } }}
                    InputLabelProps={{ sx: { fontSize: 18 } }}
                    fullWidth
                />

                {error && (
                    <Alert severity="error" role="alert" sx={{ fontSize: 18 }}>{error}</Alert>
                )}

                <Button type="submit" variant="contained" size="large" disabled={!canSubmit} sx={{ minHeight: 88 }}>
                    {submitting ? 'Activation…' : 'Activer ce poste'}
                </Button>

                <Typography sx={{ fontSize: 16, color: palette.textSecondary }}>
                    Le code se crée dans l&apos;ERP : Magasin → Caisse de secours → Postes
                </Typography>
            </Paper>
        </Box>
    );
}
